/** Client-side world state: one snapshot, then deltas over the WebSocket. Svelte 5 runes. */

import { api, token } from "./api";

export interface Pos { lat: number; lon: number }
export type Structure = any;
export type PlayerPublic = any;

export const world = $state({
  connected: false,
  /** Structure ids changed/removed since the map last consumed them — so the map patches instead of rebuilding 10k features. */
  pendingStructures: new Set<number>(),
  pendingRemovedStructures: new Set<number>(),
  structuresFull: true, // snapshot arrived: the map must rebuild everything once
  /** First snapshot received — the game is on screen. Before this the boot overlay shows. */
  ready: false,
  /** performance.now() when the current connection attempt started, and when we last went quiet. */
  connectStartedAt: 0,
  disconnectedAt: 0,
  /** No message from the server for a few seconds while "connected" — a half-open socket, being replaced. */
  stale: false,
  now: 0,
  me: null as any,
  account: null as { accountId: string; isAdmin: boolean } | null,
  players: new Map<number, PlayerPublic>(),
  alliances: new Map<number, any>(),
  treaties: new Map<number, any>(),
  structures: new Map<number, Structure>(),
  /** My own plans only — the server never sends anyone else's. */
  blueprints: new Map<number, any>(),
  missiles: new Map<number, any>(),
  interceptors: new Map<number, any>(),
  loots: new Map<number, any>(),
  radiations: new Map<number, any>(),
  events: [] as { at: number; message: string; sound: string }[],
  balance: null as any,
  /** Server flags: whether this deployment accepts browser location / dev tokens (preview only). */
  browserLocation: false,
  devTokens: false,
  version: 0, // bumped on every change so cheap $effects can depend on one number
  staticVersion: 0, // players / structures / loot / radiation changed
  flightVersion: 0, // missiles added or removed
  build: "" as string, // server build id, for auto-update
});

// debugging handle (harmless in production; nothing secret lives here)
(globalThis as any).__launch = world;
(globalThis as any).__fx = [] as any[];

export async function loadServerFlags() {
  try { const h = await (await fetch("/api/health")).json(); world.browserLocation = !!h.browserLocation; world.devTokens = !!h.devTokens; world.build = String(h.build ?? ""); } catch { /* offline */ }
}

/** Auto-update: when the server's build id changes, reload (after a short notice). */
export function watchForUpdates(onUpdate: () => void) {
  setInterval(async () => {
    try {
      const h = await (await fetch("/api/health", { cache: "no-store" })).json();
      if (world.build && h.build && h.build !== world.build) { onUpdate(); setTimeout(() => location.reload(), 2500); }
    } catch { /* offline */ }
  }, 60_000);
}

let ws: WebSocket | null = null;
type EventHook = (e: { at: number; message: string; sound: string }) => void;
type LootHook = (loot: { pos: Pos; value: number }, collector: PlayerPublic | null) => void;
const eventHooks = new Set<EventHook>(), lootHooks = new Set<LootHook>();
export const onEvent = (fn: EventHook) => { eventHooks.add(fn); return () => eventHooks.delete(fn); };
export const onLootCollected = (fn: LootHook) => { lootHooks.add(fn); return () => lootHooks.delete(fn); };
const distKm = (a: Pos, b: Pos) => { const r = Math.PI / 180, x = (b.lon - a.lon) * r * Math.cos(((a.lat + b.lat) / 2) * r), y = (b.lat - a.lat) * r; return 6372.8 * Math.sqrt(x * x + y * y); };
let retry = 1000;

function bump() { world.version++; }

function applySnapshot(s: any) {
  world.now = s.now;
  world.ready = true; world.disconnectedAt = 0;
  world.staticVersion++; world.flightVersion++;
  world.me = s.me;
  world.players = new Map(s.players.map((p: any) => [p.id, p]));
  world.alliances = new Map(s.alliances.map((a: any) => [a.id, a]));
  world.treaties = new Map(s.treaties.map((t: any) => [t.id, t]));
  world.structures = new Map(s.structures.map((x: any) => [x.id, x]));
  world.blueprints = new Map((s.blueprints ?? []).map((x: any) => [x.id, x]));
  world.structuresFull = true; world.pendingStructures.clear(); world.pendingRemovedStructures.clear();
  world.missiles = new Map(s.missiles.map((m: any) => [m.id, { ...m, seenAt: s.now, launch: m.origin ?? world.missiles.get(m.id)?.launch ?? m.pos }]));
  world.interceptors = new Map(s.interceptors.map((i: any) => [i.id, i]));
  world.loots = new Map(s.loots.map((l: any) => [l.id, l]));
  world.radiations = new Map(s.radiations.map((r: any) => [r.id, r]));
  bump();
}

function applyDelta(d: any) {
  world.now = d.now;
  let staticChanged = d.players.length + d.structures.length + d.loots.length + d.radiations.length > 0;
  // The server sends my whole plan list when it changes, so replace rather than merge: a plan that
  // was built or removed has to disappear, and a merge would leave a ghost on the map forever.
  if (d.blueprints) { world.blueprints = new Map(d.blueprints.map((b: any) => [b.id, b])); staticChanged = true; }
  let flightChanged = d.missiles.length > 0;
  for (const p of d.players) world.players.set(p.id, p);
  for (const s of d.structures) { world.structures.set(s.id, s); world.pendingStructures.add(s.id); }
  for (const m of d.missiles) world.missiles.set(m.id, { ...m, seenAt: d.now, launch: m.origin ?? world.missiles.get(m.id)?.launch ?? m.pos });
  for (const i of d.interceptors) { const cur = world.interceptors.get(i.id); if (cur) cur.pos = { lat: i.lat * 180 / Math.PI, lon: i.lon * 180 / Math.PI }; }
  for (const l of d.loots) world.loots.set(l.id, l);
  // gold that left the 50-mile window around the player — drop silently (no collection animation)
  if (d.lootsHidden?.length) { for (const id of d.lootsHidden) world.loots.delete(id); staticChanged = true; }
  for (const r of d.radiations) world.radiations.set(r.id, r);
  for (const e of d.removed) {
    if (e.entity === "missile") flightChanged = true; else if (e.entity !== "interceptor") staticChanged = true;
    if (e.entity === "structure") { world.pendingRemovedStructures.add(e.id); world.pendingStructures.delete(e.id); }
    if (e.entity === "loot") {
      const loot = world.loots.get(e.id);
      // collected (not expired) if a live player stands within the salvage distance
      if (loot && loot.pos && loot.expiryMs > 5000) {
        let best: PlayerPublic | null = null, bestD = 0.2;
        for (const p of world.players.values()) { if (!p.pos || p.dead) continue; const dd = distKm(p.pos, loot.pos); if (dd < bestD) { bestD = dd; best = p; } }
        (globalThis as any).__fx.push({ id: e.id, value: loot.value, collector: best?.name ?? null, bestD });
        if (best) for (const fn of lootHooks) fn({ pos: loot.pos, value: loot.value }, best);
      }
    }
    const m: Record<string, Map<number, any>> = { player: world.players, missile: world.missiles, interceptor: world.interceptors, structure: world.structures, loot: world.loots, radiation: world.radiations, alliance: world.alliances, treaty: world.treaties };
    m[e.entity]?.delete(e.id);
  }
  if (d.events.length) {
    world.events.push(...d.events);
    if (world.events.length > 60) world.events.splice(0, world.events.length - 60);
    for (const e of d.events) for (const fn of eventHooks) fn(e);
  }
  if (staticChanged) world.staticVersion++;
  if (flightChanged) world.flightVersion++;
  bump();
}

export async function connect() {
  world.connectStartedAt = performance.now();
  const t = await token();
  if (!t) return;
  if (!world.balance) world.balance = await api("/api/balance");
  const me = await api("/api/me");
  world.account = { accountId: me.accountId, isAdmin: me.isAdmin };
  world.me = me.player;
  const proto = location.protocol === "https:" ? "wss" : "ws";
  ws = new WebSocket(`${proto}://${location.host}/ws?token=${encodeURIComponent(t)}`);
  (globalThis as any).__ws = ws; // debugging handle
  const mine = ws;
  ws.onopen = () => { world.connected = true; world.stale = false; retry = 1000; lastMessageAt = performance.now(); };
  ws.onmessage = (ev) => {
    lastMessageAt = performance.now(); if (world.stale) world.stale = false;
    if (ev.data === "pong") return;
    const msg = JSON.parse(ev.data);
    if (msg.type === "snapshot") applySnapshot(msg);
    else if (msg.type === "delta") applyDelta(msg);
    else if (msg.type === "me") { world.me = msg.player; bump(); }
  };
  ws.onclose = () => {
    if (ws !== mine) return; // an old socket we already replaced
    world.connected = false; world.stale = false; if (!world.disconnectedAt) world.disconnectedAt = performance.now();
    setTimeout(connect, retry);
    retry = Math.min(retry * 2, 30_000);
  };
  startWatchdog();
}

/**
 * The server sends a delta every tick (1 s). A socket that is open but silent is a half-open one —
 * a phone that switched networks or came back from the background never gets a close event for it,
 * and the map keeps animating from the last state while timers freeze and lists go stale (Cam,
 * 2026-09-02). So: ping after 3 s of silence, mark stale after 5 s, replace the socket after 10 s;
 * and on return to the foreground, check immediately.
 */
let lastMessageAt = 0;
let watchdogStarted = false;
function silentFor() { return performance.now() - lastMessageAt; }
function replaceSocket(why: string) {
  const old = ws;
  if (!old) return;
  console.warn(`[ws] ${why} — reconnecting`);
  ws = null; world.connected = false; world.stale = false; if (!world.disconnectedAt) world.disconnectedAt = performance.now();
  try { old.onclose = null; old.close(); } catch { /* already dead */ }
  retry = 500;
  void connect();
}
function startWatchdog() {
  if (watchdogStarted) return;
  watchdogStarted = true;
  setInterval(() => {
    if (!ws) return;
    if (ws.readyState === WebSocket.CONNECTING) return;
    if (ws.readyState !== WebSocket.OPEN) { if (ws.readyState === WebSocket.CLOSED) replaceSocket("socket closed without an event"); return; }
    const quiet = silentFor();
    if (quiet > 10_000) replaceSocket(`no message for ${Math.round(quiet / 1000)} s`);
    else if (quiet > 5_000) { if (!world.stale) world.stale = true; try { ws.send("ping"); } catch { /* ignore */ } }
    else if (quiet > 3_000) { try { ws.send("ping"); } catch { /* ignore */ } }
  }, 1000);
  setInterval(() => { try { ws?.readyState === WebSocket.OPEN && ws.send("ping"); } catch { /* ignore */ } }, 45_000);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") return;
    // back in the foreground: anything older than a couple of ticks is suspect — don't wait for the timer
    if (!ws || ws.readyState !== WebSocket.OPEN || silentFor() > 2_500) replaceSocket("foreground after a gap");
  });
  window.addEventListener("online", () => { if (!ws || ws.readyState !== WebSocket.OPEN || silentFor() > 2_500) replaceSocket("network back"); });
}

/** "Retry now" from the boot/timeout screen: drop whatever socket we have and connect again immediately. */
export function reconnectNow() { retry = 300; if (ws) replaceSocket("retry requested"); else void connect(); }

export function refreshMe(me: any) { if (me) { world.me = me; bump(); } }

export const missileType = (id: number) => world.balance?.weapons.missiles.find((m: any) => m.id === id);
export const interceptorType = (id: number) => world.balance?.weapons.interceptors.find((m: any) => m.id === id);
